import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import OlMap from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { FeatureLike } from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import { defaults as defaultControls } from 'ol/control';
import { fromLonLat, getPointResolution, transform } from 'ol/proj';
import { Site } from 'src/app/bo/site';
import { HoleDTO, HoleService } from 'src/app/services/hole.service';
import { HttpService } from 'src/app/services/http.service';
import { OrthophotoMapService } from 'src/app/services/map.services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TerrainLoaderService } from 'src/app/services/terrain-loader.service';
import { BlastEquipmentLayer } from 'src/app/components/carte/layers/BlastEquipmentLayer';
import { DxfServiceService } from 'src/app/services/dxf-service.service';
import { LoggerService } from 'src/app/services/logger.service';
import { BaseLayers } from './layers/base-layers';
import { BlastPolygonsLayer } from './layers/blast-polygons.layer';
import { ControlPointsLayer } from './layers/control-points.layer';
import { HolesLayer } from './layers/holes.layer';
import { Map3dView } from './layers/map-3d.view';
import { MeasureLayer } from './layers/measure.layer';
import { SitePolygonsLayer } from './layers/site-polygons.layer';
import { ZonesOfInterestLayer } from './layers/zones-of-interest.layer';
import {
  DEFAULT_SITE_ZOOM,
  FALLBACK_CENTER_LON_LAT,
  FEATURE_BLAST_ID,
  FEATURE_BLAST_NAME,
  FEATURE_HOLE,
  FEATURE_KIND,
  FEATURE_LABEL,
  FEATURE_POLYGON_ID,
  FEATURE_POLYGON_TYPE,
  HOLES_MAX_RESOLUTION,
  MAP_PROJECTION,
  POPUP_CHROME_PX,
  POPUP_TOP_RESERVED_PX,
  SCALE_BAR_TARGET_PX,
  Z_INDEX,
  WGS84_PROJECTION,
} from './site-map.constants';
import { SiteMapStore } from './site-map.store';
import {
  FocusBlastRequest,
  SiteMapBlast,
  SiteMapDrawShape,
  SiteMapFeatureKind,
  SiteMapTool,
  SiteMapViewMode,
  SiteProjection,
} from './site-map.types';
import { SiteMapDrawDialogComponent } from './ui/draw-dialog/draw-dialog.component';
import { SiteMapEquipmentPanelComponent } from './ui/equipment-panel/equipment-panel.component';
import { SiteMapFilterPanelComponent } from './ui/filter-panel/filter-panel.component';
import { SiteMapHoleLegendComponent } from './ui/hole-legend/hole-legend.component';
import { SiteMapLayersPanelComponent } from './ui/layers-panel/layers-panel.component';
import { SiteMapHolesPanelComponent } from './ui/holes-panel/holes-panel.component';
import { SiteMapKpiPanelComponent } from './ui/kpi-panel/kpi-panel.component';
import { SiteMapPopupComponent } from './ui/map-popup/map-popup.component';
import { SiteMapRightMenuComponent } from './ui/right-menu/right-menu.component';
import { normalizeEpsgCode } from './utils/projection';

interface MapTooltip {
  text: string;
  x: number;
  y: number;
}

@Component({
  selector: 'app-site-map',
  standalone: true,
  templateUrl: './site-map.component.html',
  styleUrls: ['./site-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SiteMapStore],
  imports: [
    SiteMapRightMenuComponent,
    SiteMapDrawDialogComponent,
    SiteMapKpiPanelComponent,
    SiteMapHolesPanelComponent,
    SiteMapEquipmentPanelComponent,
    SiteMapLayersPanelComponent,
    SiteMapFilterPanelComponent,
    SiteMapHoleLegendComponent,
    SiteMapPopupComponent,
    TranslateModule,
  ],
})
export class SiteMapComponent {
  readonly site = input<Site | null>(null);
  readonly blasts = input<SiteMapBlast[]>([]);
  readonly visibleBlastIds = input<number[]>([]);
  readonly focusRequest = input<FocusBlastRequest | null>(null);
  readonly showDetailsLink = input(true);
  readonly openBlast = output<number>();
  readonly selectedBlastChange = output<number | null>();

  protected readonly store = inject(SiteMapStore);
  private readonly http = inject(HttpService);
  private readonly holeService = inject(HoleService);
  private readonly orthophotoService = inject(OrthophotoMapService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly logger = inject(LoggerService);
  private readonly dxfService = inject(DxfServiceService);
  private readonly terrainLoader = inject(TerrainLoaderService);

  private readonly mapTarget = viewChild.required<ElementRef<HTMLDivElement>>('mapTarget');
  private readonly popupElement = viewChild.required<ElementRef<HTMLDivElement>>('popupElement');
  private readonly map3dTarget = viewChild.required<ElementRef<HTMLDivElement>>('map3dTarget');

  protected readonly scaleLabel = signal('');
  protected readonly scaleWidth = signal(0);
  protected readonly tooltip = signal<MapTooltip | null>(null);
  private readonly pointer = signal<Coordinate | null>(null);
  protected readonly coordinates = computed(() => this.formatCoordinates(this.pointer(), this.store.site()));
  protected readonly popupMaxHeight = signal<number | null>(null);

  protected readonly map3dLoading = signal(false);
  private readonly mapReady = signal(false);
  private map3d: Map3dView | null = null;
  private mapboxToken = '';
  private map3dSiteId: number | null = null;
  private map!: OlMap;
  private baseLayers!: BaseLayers;
  private zonesOfInterest!: ZonesOfInterestLayer;
  private blastPolygons!: BlastPolygonsLayer;
  private holes!: HolesLayer;
  private controlPoints!: ControlPointsLayer;
  private measure!: MeasureLayer;
  private equipmentLayer: BlastEquipmentLayer | null = null;
  private sitePolygons!: SitePolygonsLayer;
  private popupOverlay!: Overlay;
  private resizeObserver?: ResizeObserver;
  private hovered: FeatureLike | null = null;
  private centeredSiteId: number | null = null;
  private holePopupRequest = 0;
  private selectedBlastId: number | null = null;
  private pendingFitBlastId: number | null = null;

  constructor() {
    effect(() => this.store.site.set(this.site()));
    effect(() => this.store.blasts.set(this.blasts()));
    effect(() => this.store.visibleBlastIds.set(this.visibleBlastIds()));

    afterNextRender(() => {
      this.initMap();
      this.mapReady.set(true);
    });
    inject(DestroyRef).onDestroy(() => this.disposeMap());

    this.syncLayersWithStore();
  }

  protected setViewMode(mode: SiteMapViewMode): void {
    this.store.setViewMode(mode);
  }

  protected zoomBy(delta: number): void {
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() ?? 0) + delta, duration: 250 });
  }

  private initMap(): void {
    const target = this.mapTarget().nativeElement;
    this.map = new OlMap({
      target,
      controls: defaultControls({ zoom: false }),
      view: new View({ center: fromLonLat([0, 0]), zoom: DEFAULT_SITE_ZOOM }),
    });

    this.baseLayers = new BaseLayers(this.map);
    this.zonesOfInterest = new ZonesOfInterestLayer(this.map);
    this.blastPolygons = new BlastPolygonsLayer(this.map, this.http);
    this.holes = new HolesLayer(this.map, this.http, legends => this.store.holeLegends.set(legends));
    this.controlPoints = new ControlPointsLayer(this.map);
    this.measure = new MeasureLayer(this.map);
    this.sitePolygons = new SitePolygonsLayer(this.map, this.dxfService);
    this.measure.circleDrawn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(circle => {
      if (this.store.drawShape() !== 'circle') return;
      this.store.drawnGeometry.set({ coordinates: circleToPolygon(circle.center, circle.radius), circle });
    });
    this.measure.polygonDrawn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(coordinates => {
      if (this.store.drawShape() !== 'polygon') return;
      this.store.drawnGeometry.set({ coordinates, circle: null });
    });

    this.popupOverlay = new Overlay({ element: this.popupElement().nativeElement });
    this.map.addOverlay(this.popupOverlay);

    this.map.getView().on('change:resolution', () => this.onResolutionChange());
    this.map.on('moveend', () => this.refreshHoles());
    this.map.on('pointermove', event => this.onPointerMove(event));
    this.map.on('click', event => this.onMapClick(event));
    target.addEventListener('mouseleave', this.onPointerLeave);

    this.resizeObserver = new ResizeObserver(() => {
      this.map.updateSize();
      const height = target.clientHeight;
      this.popupMaxHeight.set(height
        ? Math.max(0, height / 2 - POPUP_TOP_RESERVED_PX - POPUP_CHROME_PX)
        : null);
      this.applyPendingFocus();
    });
    this.resizeObserver.observe(target);
    this.onResolutionChange();

    this.orthophotoService.getMapBoxPublicKey()
      .then(token => {
        if (!token) return;
        this.mapboxToken = token;
        this.baseLayers.setSatelliteToken(token);
        // Le token arrive de façon asynchrone : si la 3D a été demandée entre-temps, on la lance.
        this.sync3d(this.store.viewMode() === '3d', this.store.site(), this.store.visibleBlastIds());
      })
      .catch(() => {});
  }

  /** Répercute l'état du store sur les calques OpenLayers ; chaque effet ne touche qu'une responsabilité. */
  private syncLayersWithStore(): void {
    effect(() => {
      const mode = this.store.viewMode();
      // La 3D n'a pas de fond OpenLayers : les calques 2D gardent leur dernier réglage.
      if (this.mapReady() && mode !== '3d') this.baseLayers.setViewMode(mode);
    });
    effect(() => {
      if (!this.mapReady()) return;
      const is3d = this.store.viewMode() === '3d';
      const site = this.store.site();
      const blastIds = this.store.visibleBlastIds();
      untracked(() => this.sync3d(is3d, site, blastIds));
    });
    effect(() => {
      if (!this.mapReady()) return;
      const orthophotos = this.store.orthophotos();
      untracked(() => this.baseLayers.setOrthophotos(orthophotos, this.store.orthophotosVisible()));
    });
    effect(() => {
      if (this.mapReady()) this.baseLayers.setOrthophotosVisible(this.store.orthophotosVisible());
    });
    effect(() => {
      if (!this.mapReady()) return;
      const site = this.store.site();
      const projection = this.store.siteProjection();
      untracked(() => this.centerOnSite(site, projection));
    });
    effect(() => {
      if (this.mapReady()) this.controlPoints.setPoints(this.store.controlPoints());
    });
    effect(() => {
      if (this.mapReady()) this.controlPoints.setVisible(this.store.controlPointsVisible());
    });
    effect(() => {
      if (this.mapReady()) this.zonesOfInterest.setZones(this.store.zonesOfInterest());
    });
    effect(() => {
      if (this.mapReady()) this.zonesOfInterest.setVisible(this.store.zonesOfInterestVisible());
    });
    effect(() => {
      if (this.mapReady()) this.blastPolygons.setVisibleTypes(this.store.polygonTypes());
    });
    effect(() => {
      if (this.mapReady()) this.blastPolygons.setColoring(this.store.polygonColorOf());
    });
    effect(() => {
      if (!this.mapReady()) return;
      const siteId = this.store.siteId();
      const blastIds = this.store.visibleBlastIds();
      if (siteId === null) return;
      untracked(() => this.blastPolygons.showBlasts(siteId, blastIds).then(() => {
        this.refreshHoles();
        this.applyPendingFocus();
      }));
    });
    effect(() => {
      if (!this.mapReady()) return;
      this.store.holesShown();
      untracked(() => this.refreshHoles());
    });
    effect(() => {
      if (this.mapReady()) this.holes.setFilters(this.store.holeStatusFilter(), this.store.holeFeatureFilter());
    });
    effect(() => {
      if (this.mapReady()) this.popupOverlay.setPosition(this.store.popup()?.coordinate);
    });
    effect(() => {
      if (!this.mapReady()) return;
      const tool = this.store.activeTool();
      const shape = this.store.drawShape();
      untracked(() => this.applyTool(tool, shape));
    });
    effect(() => {
      // Le formulaire est ouvert : la forme tracée reste affichée mais on ne peut plus en dessiner d'autre.
      if (this.mapReady() && this.store.drawnGeometry()) this.measure.suspend();
    });
    effect(() => {
      if (!this.mapReady()) return;
      const siteId = this.store.siteId();
      this.store.dxfReloadTick();
      untracked(() => this.loadDxfLayers(siteId));
    });
    effect(() => {
      const layers = this.store.dxfLayers();
      if (this.mapReady()) layers.forEach(layer => this.sitePolygons.setVisibility(layer.id, layer.isVisible));
    });
    effect(() => {
      // Le panneau Équipement n'existe que sur le site équipé : on le ferme si l'on change de site.
      if (this.store.activePanel() === 'equipment' && !this.store.equipmentEnabled()) this.store.activePanel.set(null);
    });
    effect(() => {
      if (!this.mapReady()) return;
      const open = this.store.activePanel() === 'equipment' && this.store.equipmentEnabled();
      const siteId = this.store.siteId();
      untracked(() => this.syncEquipmentLayer(open, siteId));
    });
    effect(() => {
      const open = this.store.activePanel() === 'equipment' && this.store.equipmentEnabled();
      const date = this.store.equipmentDate();
      const shift = this.store.equipmentShift();
      const equipments = this.store.equipments();
      if (!this.mapReady() || !open || !date || shift === null || !equipments.length) return;
      untracked(() => this.equipmentLayer?.getEquipmentPositions({
        selectedEquipments: equipments,
        selectedDate: date,
        selectedDateRange: [],
        selectedShift: shift,
        timeSelection: 'shift',
      }));
    });
    effect(() => {
      const created = this.store.polygonCreated();
      if (!this.mapReady() || !created) return;
      untracked(() => {
        const siteId = this.store.siteId();
        if (siteId === null) return;
        this.blastPolygons.reloadBlast(siteId, created.blastId).then(() => this.refreshHoles());
      });
    });
    effect(() => {
      const blastId = this.store.popup()?.blastId ?? null;
      if (blastId === this.selectedBlastId) return;
      this.selectedBlastId = blastId;
      untracked(() => this.selectedBlastChange.emit(blastId));
    });
    effect(() => {
      const request = this.focusRequest();
      if (!this.mapReady() || !request) return;
      untracked(() => {
        if (request.mode === 'fit') {
          this.store.closePopup();
          this.pendingFitBlastId = request.blastId;
          this.applyPendingFocus();
        } else {
          this.focusBlast(request.blastId);
        }
      });
    });
  }

  /** Charge les couches DXF du site et alimente le panneau ; une réponse tardive d'un autre site est ignorée. */
  private loadDxfLayers(siteId: number | null): void {
    this.store.dxfLayers.set([]);
    if (siteId === null) return;
    const epsg = Number(this.store.site()?.epsg) || undefined;
    this.sitePolygons.load(siteId, epsg)
      .then(polygones => {
        if (polygones && this.store.siteId() === siteId) this.store.dxfLayers.set(polygones.map(polygone => ({ ...polygone })));
      })
      .catch(() => {
        if (this.store.siteId() === siteId) this.store.dxfLayers.set([]);
      });
  }

  /** Le calque d'équipements est créé à la première ouverture du panneau ; fermé, il est vidé et masqué. */
  private syncEquipmentLayer(open: boolean, siteId: number | null): void {
    if (!open) {
      this.equipmentLayer?.clear();
      this.equipmentLayer?.setEquipmentsVisible(false);
      return;
    }
    if (siteId === null) return;
    if (!this.equipmentLayer) {
      this.equipmentLayer = new BlastEquipmentLayer(this.map, this.http, this.translate, this.logger, Z_INDEX.equipments);
      this.equipmentLayer.dataLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() =>
        this.store.equipments.set(this.equipmentLayer?.getEquipments() ?? []));
    }
    this.equipmentLayer.setEquipmentsVisible(true);
    this.equipmentLayer.fetchEquipments(siteId);
  }

  /** La mesure démarre aussitôt ; les outils de création attendent le choix de la forme (polygone ou cercle). */
  private applyTool(tool: SiteMapTool | null, shape: SiteMapDrawShape | null): void {
    this.tooltip.set(null);
    if (tool === 'ruler') {
      this.measure.start('line');
    } else if ((tool === 'polygon' || tool === 'zone') && shape) {
      this.measure.start(shape === 'circle' ? 'circle' : 'area');
    } else {
      this.measure.stop();
    }
  }

  /**
   * Le cadrage attend que les polygones du tir soient chargés et que la carte soit affichée (taille nulle tant
   * qu'elle est masquée) ; il est rejoué à la fin de chaque chargement et à chaque redimensionnement.
   */
  private applyPendingFocus(): void {
    const blastId = this.pendingFitBlastId;
    if (blastId === null || !this.store.visibleBlastIds().includes(blastId)) return;
    const size = this.map.getSize();
    if (!size || !size[0] || !size[1]) return;
    const extent = this.blastPolygons.blastExtent(blastId);
    if (!extent) return;
    this.pendingFitBlastId = null;
    this.store.closePopup();
    this.map.getView().fit(extent, { padding: [80, 80, 80, 80], maxZoom: 19, duration: 500 });
  }

  /** Centre la vue sur le site une seule fois par site, dès que sa projection est résolue (repli sur la France sans EPSG). */
  private centerOnSite(site: Site | null, projection: SiteProjection | null): void {
    if (!site || site.siteId === this.centeredSiteId) return;
    const siteCode = normalizeEpsgCode(site.epsg);
    if (!projection || projection.code !== siteCode) return;

    this.centeredSiteId = site.siteId;
    const center = projection.registered ? this.toMapCoordinate([site.coordX, site.coordY], siteCode) : null;
    this.baseLayers.setSiteCenter(center);
    this.store.closePopup();

    const view = this.map.getView();
    view.setCenter(center ?? fromLonLat(FALLBACK_CENTER_LON_LAT));
    view.setZoom(DEFAULT_SITE_ZOOM);

    // Au rechargement, la projection du site peut arriver après le cadrage sur un tir : on le rejoue.
    const request = this.focusRequest();
    if (request?.mode === 'fit') {
      this.pendingFitBlastId = request.blastId;
      this.applyPendingFocus();
    }
  }

  /** Échelle graphique réelle (résolution au centre de la vue) et seuil d'affichage des trous. */
  private onResolutionChange(): void {
    const view = this.map.getView();
    const resolution = view.getResolution();
    const center = view.getCenter();
    if (!resolution || !center) return;

    const metersPerPixel = getPointResolution(view.getProjection(), resolution, center, 'm');
    const length = niceScaleLength(metersPerPixel * SCALE_BAR_TARGET_PX);
    this.scaleLabel.set(length >= 1000 ? `${length / 1000} km` : `${length} m`);
    this.scaleWidth.set(length / metersPerPixel);
    this.store.zoomedIn.set(resolution < HOLES_MAX_RESOLUTION);
  }

  /** Les trous ne sont chargés et affichés que pour les tirs cochés dont un polygone est visible à l'écran. */
  private refreshHoles(): void {
    const siteId = this.store.siteId();
    if (siteId === null || !this.store.holesShown()) {
      this.holes.hide();
      return;
    }
    const extent = this.map.getView().calculateExtent(this.map.getSize());
    this.holes.show(siteId, this.blastPolygons.blastIdsInExtent(extent));
  }

  private onPointerMove(event: MapBrowserEvent): void {
    if (event.dragging) return;
    this.pointer.set(event.coordinate);
    if (this.store.activeTool()) return;

    const feature = this.map.forEachFeatureAtPixel(event.pixel, candidate => candidate) ?? null;
    this.map.getTargetElement().style.cursor = feature ? 'pointer' : '';
    this.setHovered(feature);

    const text = feature ? this.tooltipText(feature) : '';
    const pointerEvent = event.originalEvent as MouseEvent;
    this.tooltip.set(text ? { text, x: pointerEvent.clientX + 10, y: pointerEvent.clientY - 25 } : null);
  }

  private readonly onPointerLeave = (): void => {
    this.setHovered(null);
    this.tooltip.set(null);
  };

  private setHovered(feature: FeatureLike | null): void {
    if (feature === this.hovered) return;
    this.hovered = feature;
    const kind: SiteMapFeatureKind | undefined = feature?.get(FEATURE_KIND);
    this.blastPolygons.setHovered(kind === 'blast-polygon' ? feature : null);
    this.holes.setHovered(kind === 'hole' ? feature : null);
    this.zonesOfInterest.setHovered(kind === 'zone-of-interest' ? feature : null);
  }

  private tooltipText(feature: FeatureLike): string {
    switch (feature.get(FEATURE_KIND) as SiteMapFeatureKind) {
      case 'blast-polygon': {
        const blast = this.store.blastsById().get(feature.get(FEATURE_BLAST_ID));
        return [blast?.nom || 'Blast', formatDayMonthYear(blast?.dateTirPrevisionnelle)].filter(Boolean).join('\n');
      }
      case 'hole':
        return (feature.get(FEATURE_HOLE) as HoleDTO)?.name || 'Hole';
      case 'zone-of-interest':
        return feature.get(FEATURE_LABEL) || 'Zone of Interest';
      default:
        return '';
    }
  }

  /** Priorité au trou sous le curseur, puis au polygone de tir ; un clic dans le vide ferme la popup. */
  private onMapClick(event: MapBrowserEvent): void {
    if (this.store.activeTool()) return;
    const features = this.map.getFeaturesAtPixel(event.pixel);
    const hole = features.find(feature => feature.get(FEATURE_KIND) === 'hole');
    if (hole) {
      this.openHolePopup(hole, event.coordinate);
      return;
    }
    const polygon = features.find(feature => feature.get(FEATURE_KIND) === 'blast-polygon');
    if (polygon) {
      this.openBlastPopup(polygon.get(FEATURE_BLAST_ID), event.coordinate, true, polygon);
      return;
    }
    this.store.closePopup();
  }

  private openBlastPopup(blastId: number, coordinate: Coordinate, pan: boolean, polygon?: FeatureLike): void {
    this.holePopupRequest++;
    if (pan) this.map.getView().animate({ center: coordinate });
    this.store.openPopup({
      kind: 'blast',
      blastId,
      coordinate,
      polygonId: polygon?.get(FEATURE_POLYGON_ID) ?? null,
      polygonType: polygon?.get(FEATURE_POLYGON_TYPE) ?? null,
    });
  }

  /** La popup s'ouvre immédiatement ; le détail du chargement (GraphQL) la complète dès qu'il arrive. */
  private openHolePopup(feature: FeatureLike, coordinate: Coordinate): void {
    const request = ++this.holePopupRequest;
    const hole: HoleDTO = feature.get(FEATURE_HOLE);
    this.map.getView().animate({ center: coordinate });
    this.store.openPopup({
      kind: 'hole',
      blastId: feature.get(FEATURE_BLAST_ID),
      blastName: feature.get(FEATURE_BLAST_NAME) ?? '',
      hole,
      details: null,
      coordinate,
    });

    this.holeService.GetHoleDetail(hole.publicGuid).then(details => {
      const popup = this.store.popup();
      if (request !== this.holePopupRequest || popup?.kind !== 'hole') return;
      this.store.openPopup({ ...popup, details });
    });
  }

  /** Centre la carte sur le tir cliqué dans l'arbre puis ouvre sa popup ; sans polygone chargé, la popup s'ouvre près du centre. */
  private focusBlast(blastId: number): void {
    const view = this.map.getView();
    const center = this.blastPolygons.blastCenter(blastId);
    if (center) {
      view.animate({ center, duration: 500 }, () => this.openBlastPopup(blastId, center, false));
      return;
    }
    const viewCenter = view.getCenter();
    if (viewCenter) this.openBlastPopup(blastId, offsetForZoom(viewCenter, view.getZoom() ?? 0), false);
  }

  private formatCoordinates(pointer: Coordinate | null, site: Site | null) {
    const projection = normalizeEpsgCode(site?.epsg) ?? WGS84_PROJECTION;
    const isDegree = projection === WGS84_PROJECTION;
    const [x, y] = (pointer && this.fromMapCoordinate(pointer, projection)) ?? [0, 0];
    return {
      projection,
      xLabel: isDegree ? 'G' : 'X',
      yLabel: isDegree ? 'L' : 'Y',
      x: pointer ? x.toFixed(isDegree ? 4 : 0) : '0',
      y: pointer ? y.toFixed(isDegree ? 4 : 0) : '0',
    };
  }

  private toMapCoordinate(coordinate: Coordinate, projection: string): Coordinate | null {
    try {
      return transform(coordinate, projection, MAP_PROJECTION);
    } catch {
      return null;
    }
  }

  private fromMapCoordinate(coordinate: Coordinate, projection: string): Coordinate | null {
    try {
      return transform(coordinate, MAP_PROJECTION, projection);
    } catch {
      return null;
    }
  }

  /**
   * La 3D est créée à l'entrée dans le mode et détruite à la sortie (libère le contexte WebGL),
   * et reconstruite quand le site change. Les tirs visibles sont resynchronisés à chaque changement.
   */
  private sync3d(active: boolean, site: Site | null, blastIds: number[]): void {
    if (!active || !site) {
      this.destroy3d();
      return;
    }
    if (this.map3d && this.map3dSiteId !== site.siteId) this.destroy3d();
    if (this.map3d) {
      this.map3d.setBlasts(blastIds);
      return;
    }
    if (!this.mapboxToken) return;

    const view = this.map.getView();
    const center = view.getCenter();
    const lonLat = center ? transform(center, MAP_PROJECTION, WGS84_PROJECTION) as [number, number] : null;
    this.map3dSiteId = site.siteId;
    this.map3d = new Map3dView({
      site,
      token: this.mapboxToken,
      holeService: this.holeService,
      http: this.http,
      terrainLoader: this.terrainLoader,
      onLoadingChange: loading => this.map3dLoading.set(loading),
    });
    this.map3d.start(this.map3dTarget().nativeElement, lonLat, view.getZoom() ?? DEFAULT_SITE_ZOOM, blastIds)
      .catch(error => this.logger.logError(error));
  }

  private destroy3d(): void {
    this.map3d?.destroy();
    this.map3d = null;
    this.map3dSiteId = null;
  }

  private disposeMap(): void {
    this.destroy3d();
    if (!this.map) return;
    this.resizeObserver?.disconnect();
    this.map.getTargetElement()?.removeEventListener('mouseleave', this.onPointerLeave);
    this.measure.dispose();
    this.sitePolygons.dispose();
    if (this.equipmentLayer) this.map.removeLayer(this.equipmentLayer.layer);
    this.holes.dispose();
    this.blastPolygons.dispose();
    this.zonesOfInterest.dispose();
    this.controlPoints.dispose();
    this.baseLayers.dispose();
    this.map.setTarget(undefined);
  }
}

/** Anneau fermé de 64 segments approchant un cercle, dans la projection de la carte (comme l'ancienne carte). */
function circleToPolygon(center: Coordinate, radius: number): Coordinate[][] {
  const segments = 64;
  const ring: Coordinate[] = [];
  for (let i = 0; i < segments; i++) {
    const angle = (i * 2 * Math.PI) / segments;
    ring.push([center[0] + radius * Math.cos(angle), center[1] + radius * Math.sin(angle)]);
  }
  ring.push([...ring[0]]);
  return [ring];
}

/** Arrondit une longueur à 1, 2 ou 5 × 10ⁿ mètres. */
function niceScaleLength(rawLength: number): number {
  const base = Math.pow(10, Math.floor(Math.log10(rawLength)));
  const factor = rawLength / base;
  return (factor < 2 ? 1 : factor < 5 ? 2 : 5) * base;
}

/** Décalage appliqué par l'ancienne carte à la popup d'un tir sans polygone, selon le niveau de zoom. */
function offsetForZoom(coordinate: Coordinate, zoom: number): Coordinate {
  const offset = zoom < 16 ? 500 : zoom < 19 ? 100 : 20;
  return [coordinate[0] - offset, coordinate[1] - offset];
}

function formatDayMonthYear(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}
